import {useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react';
import {CioAsaProvider, type ChatMessage, type RequestConfigs} from '@constructor-io/constructorio-ui-asa';
import {SendHorizontal, ThumbsDown, ThumbsUp} from 'lucide-react';
import type {Controller} from './host/controller';
import {ConversationStorage} from './host/persistence';
import {normalizeAsaRequestConfig, withProductPricingFields} from './host/asaRequestConfig';
import {AdvisorDialog, ADVISOR_ICON} from './components/AdvisorDialog';
import {OrderedAssistant} from './components/OrderedAssistant';
import {AssistantContent} from './components/AssistantContent';
import {SuggestedQuestions} from './components/SuggestedQuestions';
import {BetaFeedbackActions} from './components/BetaFeedbackActions';
import {agentContentSettings, resolveAgentContent, type ResolvedAgentContent} from './content';
import {useTweAsaResults, type TweChatMessage} from './constructor/useTweAsaResults';
import {formatResponseDuration, formatThinkingDuration} from './timing';
import type {EventContext, Feedback} from './host/types';

export const welcomeCopy = () => resolveAgentContent().chatWindow.welcomeText;
export const STARTERS = agentContentSettings.suggestedQuestions;
type SubmitSource = 'input' | 'suggestion' | 'refinement' | 'follow-up-question';

function Conversation({controller, storage, content}: {controller: Controller; storage: ConversationStorage; content: ResolvedAgentContent}) {
  const {agentName} = content;
  const bus = controller.bus;
  const [initial] = useState(() => storage.read());
  const [votes, setVotes] = useState(initial.votes);
  const [input, setInput] = useState('');
  const [localError, setLocalError] = useState('');
  const [currentTime, setCurrentTime] = useState(() => performance.now());
  const inputRef = useRef<HTMLInputElement>(null);
  const viewport = useRef<HTMLDivElement>(null);
  const answerAnchor = useRef<{questionPositioned: boolean} | null>(null);
  const submitLock = useRef(false);
  const seenCompleted = useRef(new Set(initial.messages.filter(message => message.status === 'done').map(message => message.id)));
  const seenErrors = useRef(new Set(initial.messages.filter(message => message.status === 'error').map(message => message.id)));
  const thread = useRef(initial.threadId);
  const announcedThreads = useRef(new Set(initial.threadId ? [initial.threadId] : []));

  const {messages: live, sendMessage, retryMessage, isStreaming, clearHistory} = useTweAsaResults({
    initialThreadId: initial.threadId,
    initialMessages: initial.messages,
    onResultLoadStart: payload => {
      const context: EventContext = {
        ...bus.context,
        ...(payload.threadId && {threadId: payload.threadId}),
        ...(payload.intentResultId && {intentResultId: payload.intentResultId}),
        originalPrompt: payload.intent,
      };
      if (payload.threadId) thread.current = payload.threadId;
      bus.context = context;
      bus.emit('onResponseStarted', {originalPrompt: payload.intent}, context);
    },
  });
  const all = live;
  const latest = [...all].reverse().find(message => message.role === 'assistant');
  const contextFor = (message: ChatMessage): EventContext => ({
    threadId: message.threadId || thread.current,
    intentResultId: message.intentResultId,
    originalPrompt: message.intent,
    source: 'conversation',
  });

  useLayoutEffect(() => {
    controller.resetHandler = () => {
      clearHistory();
      storage.clear();
      setVotes({});
      setInput('');
      setLocalError('');
      thread.current = undefined;
      submitLock.current = false;
      answerAnchor.current = null;
      seenCompleted.current.clear();
      seenErrors.current.clear();
      announcedThreads.current.clear();
      bus.displayed.clear();
      bus.context = {};
      bus.emit('onConversationReset', {}, {source: 'api'});
      inputRef.current?.focus();
    };
    return () => {
      controller.resetHandler = null;
    };
  }, [controller, clearHistory, storage, bus]);

  useEffect(() => {
    const realThread = latest?.threadId;
    if (realThread && !announcedThreads.current.has(realThread)) {
      announcedThreads.current.add(realThread);
      thread.current = realThread;
      const context = contextFor(latest);
      bus.context = context;
      bus.emit('onConversationStarted', {originalPrompt: latest.intent}, context);
    }
    if (latest) bus.context = contextFor(latest);
    storage.write({threadId: thread.current, messages: all, votes});
    for (const message of live) {
      if (message.role !== 'assistant') continue;
      if (message.status === 'done' && !seenCompleted.current.has(message.id)) {
        seenCompleted.current.add(message.id);
        bus.emit('onResponseCompleted', {
          responseId: message.id,
          responseText: message.text,
          resultGroupCount: message.groups?.length || 0,
        }, contextFor(message));
      }
      if (message.status === 'error' && !seenErrors.current.has(message.id)) {
        seenErrors.current.add(message.id);
        bus.error(
          new Error('Constructor ASA stream failed. The public SDK exposes error status but not the underlying network exception; inspect the request in browser Network tools.'),
          contextFor(message),
          'CONSTRUCTOR_STREAM_FAILED',
          'asa_streaming',
        );
      }
    }
  }, [all, live, votes, bus, storage, latest]);

  useEffect(() => {
    bus.emit('onAgentLoaded', {sdkVersion: '1.5.0', restoredMessages: initial.messages.length});
    if (initial.threadId) {
      bus.emit('onConversationRestored', {restoredMessages: initial.messages.length}, {
        threadId: initial.threadId,
        source: 'conversation',
      });
    }
  }, [bus, initial.messages.length, initial.threadId]);

  useEffect(() => {
    if (!isStreaming) submitLock.current = false;
  }, [isStreaming]);

  useEffect(() => {
    if (!isStreaming) return;
    const interval = window.setInterval(() => setCurrentTime(performance.now()), 1_000);
    return () => window.clearInterval(interval);
  }, [isStreaming]);

  useLayoutEffect(() => {
    const anchor = answerAnchor.current;
    const panel = viewport.current;
    if (!anchor || !panel || anchor.questionPositioned) return;
    const question = Array.from(panel.querySelectorAll<HTMLElement>('.twe-drinks-advisor__user')).at(-1);
    if (!question) return;
    const panelBounds = panel.getBoundingClientRect();
    const baseTop = panel.scrollTop + question.getBoundingClientRect().top - panelBounds.top - 26;
    const previousFeedback = Array.from(panel.querySelectorAll<HTMLElement>('.twe-drinks-advisor__feedback')).at(-1);
    const feedbackTop = previousFeedback ? panel.scrollTop + previousFeedback.getBoundingClientRect().bottom - panelBounds.top + 16 : 0;
    panel.scrollTo({top: Math.max(0, baseTop, feedbackTop), behavior: 'instant'});
    anchor.questionPositioned = true;
  }, [all]);

  const submit = (text: string, source: SubmitSource = 'input') => {
    const userInput = text.trim();
    if (!userInput || isStreaming || submitLock.current) return;
    submitLock.current = true;
    setLocalError('');
    answerAnchor.current = {questionPositioned: false};
    const publicSource: EventContext['source'] = source === 'refinement' ? 'refinement' : source === 'input' ? 'input' : 'suggestion';
    const context: EventContext = {
      threadId: thread.current,
      originalPrompt: userInput,
      source: publicSource,
    };
    bus.context = context;
    if (source === 'input') bus.emit('onPromptSubmit', {originalPrompt: userInput, userInput}, context);
    if (source === 'suggestion' || source === 'follow-up-question') {
      bus.emit('onSuggestionClick', {originalPrompt: userInput, suggestion: userInput, location: source === 'suggestion' ? 'landing' : 'follow_up'}, context);
    }
    if (source === 'refinement') bus.emit('onRefinementClick', {originalPrompt: userInput, option: userInput}, context);
    try {
      sendMessage(userInput, source);
      setInput('');
      // On phones, retaining text-field focus keeps the software keyboard open
      // and obscures the incoming answer. This applies equally to typed, preset,
      // follow-up, and refinement prompts; desktop keeps rapid follow-up focus.
      if (window.matchMedia('(max-width: 600px)').matches) inputRef.current?.blur();
      else inputRef.current?.focus();
    } catch (error) {
      submitLock.current = false;
      setLocalError(content.response.friendlyError);
      bus.error(error, context, 'CONSTRUCTOR_SUBMIT_FAILED', 'asa_streaming');
    }
  };

  const retry = (message: TweChatMessage) => {
    if (isStreaming || !message.intent) return;
    submitLock.current = true;
    seenErrors.current.delete(message.id);
    seenCompleted.current.delete(message.id);
    setLocalError('');
    answerAnchor.current = {questionPositioned: false};
    const context = contextFor(message);
    bus.context = context;
    try {
      retryMessage(message.id);
    } catch (error) {
      submitLock.current = false;
      bus.error(error, context, 'CONSTRUCTOR_RETRY_FAILED', 'asa_streaming');
    }
  };

  const vote = (message: ChatMessage, choice: 'up' | 'down') => {
    if (votes[message.id]) return;
    setVotes(previous => ({...previous, [message.id]: choice}));
    const feedback: Feedback = {vote: choice, messageId: message.id, constructorSubmitted: false};
    bus.emit('onFeedbackSubmit', {
      feedback: choice === 'up' ? 'positive' : 'negative',
      responseId: message.id,
      feedbackDetail: feedback,
    }, contextFor(message));
  };
  const feedbackControls = (message: TweChatMessage) => message.status === 'done' ? <div className="twe-drinks-advisor__feedback" aria-label={content.response.rateAnswerAriaLabel}><button type="button" aria-label={content.response.helpfulAnswerAriaLabel} title={content.response.helpfulAnswerAriaLabel} aria-pressed={votes[message.id] === 'up'} disabled={Boolean(votes[message.id])} onClick={() => vote(message, 'up')}><ThumbsUp size={18}/></button><button type="button" aria-label={content.response.unhelpfulAnswerAriaLabel} title={content.response.unhelpfulAnswerAriaLabel} aria-pressed={votes[message.id] === 'down'} disabled={Boolean(votes[message.id])} onClick={() => vote(message, 'down')}><ThumbsDown size={18}/></button>{votes[message.id] && <span>{content.response.feedbackThanks}</span>}</div> : null;
  const responseTiming = (message: TweChatMessage) => {
    if (message.status === 'done' && typeof message.responseDurationMs === 'number' && message.completedAt) {
      return `${content.response.responseTimingPrefix} ${formatResponseDuration(message.responseDurationMs)} · ${message.completedAt}`;
    }
    if ((message.status === 'loading' || message.status === 'streaming') && typeof message.responseStartedAt === 'number') {
      return `${content.response.thinkingPrefix} ${formatThinkingDuration(message.responseStartedAt, currentTime)}`;
    }
    return null;
  };

  const hasSubmittedMessage = all.some(message => message.role === 'user');

  return <AdvisorDialog controller={controller} hasSubmittedMessage={hasSubmittedMessage}>
    <div ref={viewport} className={`twe-drinks-advisor__messages${all.length ? '' : ' twe-drinks-advisor__messages--welcome'}`} role="log" aria-label={content.chatWindow.conversationAriaLabel} aria-live="polite" aria-relevant="additions text">
      {!all.length ? <section className="twe-drinks-advisor__welcome">
        <img className="twe-drinks-advisor__welcome-icon" src={ADVISOR_ICON} alt="" width="49" height="59"/>
        <h1>{content.chatWindow.welcomeHeading}</h1><p>{content.chatWindow.welcomeText}</p>
        <SuggestedQuestions questions={content.suggestedQuestions.map(starter => ({label: starter.label, value: starter.prompt}))} onSelect={prompt => submit(prompt, 'suggestion')} disabled={isStreaming} ariaLabel={content.chatWindow.suggestedQuestionsAriaLabel} variant="welcome"/>
      </section> : all.map(message => message.role === 'user' ? <div key={message.id} className="twe-drinks-advisor__user" data-message-id={message.id}><span className="twe-drinks-advisor__user-text"><span className="twe-drinks-advisor__sr-only">You: </span>{message.text}</span>{message.submittedAt && <time className="twe-drinks-advisor__user-timestamp">{message.submittedAt}</time>}</div> : <section key={message.id} className="twe-drinks-advisor__assistant" aria-label={content.chatWindow.assistantResponseAriaLabel} data-message-id={message.id} data-status={message.status}>
        {responseTiming(message) && <p className="twe-drinks-advisor__response-timing" aria-live="off">{responseTiming(message)}</p>}
        <AssistantContent message={message}>
          <OrderedAssistant message={message} bus={bus} context={contextFor(message)} isStreaming={isStreaming} onRefinement={option => submit(option, 'refinement')} onQuestion={question => submit(question, 'follow-up-question')}/>
          {feedbackControls(message)}
        </AssistantContent>
        {message.status === 'error' && <div role="alert" className="twe-drinks-advisor__error">{content.response.friendlyError}<button type="button" disabled={isStreaming} onClick={() => retry(message)}>{content.response.retryButtonText}</button></div>}
      </section>)}
    </div>
    <footer className="twe-drinks-advisor__composer">
      {localError && <p className="twe-drinks-advisor__error" role="alert">{localError}</p>}
      <form onSubmit={event => { event.preventDefault(); submit(input); }}>
        <input ref={inputRef} type="text" value={input} onChange={event => setInput(event.target.value)} placeholder={content.chatWindow.inputPlaceholder} aria-label={content.chatWindow.inputAriaLabel} autoComplete="off"/>
        <button type="submit" className="cta-button cta-button--bravo cta-button--regular twe-drinks-advisor__send-button" aria-label={content.chatWindow.submitButtonText} title={content.chatWindow.submitButtonText} disabled={!input.trim() || isStreaming}><SendHorizontal size={19} strokeWidth={1.8} aria-hidden="true"/></button>
      </form>
      <p className="twe-drinks-advisor__ai-disclaimer">
        {content.disclaimer.text} <a href={content.disclaimer.linkUrl} target="_blank" rel="noopener noreferrer">{content.disclaimer.linkText}</a>
      </p>
    </footer>
  </AdvisorDialog>;
}

export function DrinksAdvisorApp({controller}: {controller: Controller}) {
  const dialogState = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const [hasOpened, setHasOpened] = useState(dialogState.open);
  const apiKey = controller.options.apiKey || controller.options.config?.constructor?.apiKey || import.meta.env.VITE_CONSTRUCTOR_API_KEY;
  const requestConfig = useMemo<RequestConfigs>(() => {
    const configured = withProductPricingFields(normalizeAsaRequestConfig(controller.options.asaRequestConfig));
    return {
      ...configured,
      domain: configured.domain || controller.options.config?.constructor?.domain || import.meta.env.VITE_CONSTRUCTOR_ASA_DOMAIN || 'chatbot',
    } as RequestConfigs;
  }, [controller]);
  const content = useMemo(() => resolveAgentContent(), []);
  const storage = useMemo(() => new ConversationStorage(controller.bus, `${apiKey}:${requestConfig.domain}`), [controller, apiKey, requestConfig.domain]);
  useEffect(() => {
    if (dialogState.open) setHasOpened(true);
  }, [dialogState.open]);
  return <>
    <BetaFeedbackActions controller={controller}/>
    {hasOpened && <CioAsaProvider apiKey={apiKey} staticRequestConfigs={requestConfig}><Conversation controller={controller} storage={storage} content={content}/></CioAsaProvider>}
  </>;
}
